import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { KAPHY_BASE_URL } from "../service/SignUpService";

const AuthContext = createContext();

const url = KAPHY_BASE_URL;
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        const storedToken = localStorage.getItem("token");

        if (storedUser && storedToken) {
          setUser(JSON.parse(storedUser));
          setToken(storedToken);
          axios.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${storedToken}`;
        }
      } catch (err) {
        console.error("Failed to initialize auth:", err);
        // Clear invalid data
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        delete axios.defaults.headers.common["Authorization"];
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${url}/api/users/login`, {
        email,
        password,
      });
      const { token, ...userData } = response.data;

      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("token", token);

      setUser(userData);
      setToken(token);

      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      return userData;
    } catch (error) {
      throw new Error(error.response?.data?.error || "Login failed");
    }
  };

  const logout = useCallback(() => {
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      setUser(null);
      setToken(null);
      delete axios.defaults.headers.common["Authorization"];
      return true;
    } catch (error) {
      console.error("Logout failed:", error);
      return false;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      if (!token || !user?._id) {
        return null;
      }

      const response = await axios.get(`${url}/api/users/list/${user._id}`);
      const updatedUser = {
        _id: response.data._id,
        email: response.data.email,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        role: response.data.role,
        isVerified: response.data.isVerified,
        isAdmin: response.data.isAdmin,
        supperAdmin: response.data.supperAdmin,
        createdAt: response.data.createdAt,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error("Failed to refresh user data:", error);
      if (error.response?.status === 401 || error.response?.status === 404) {
        // Token is invalid or user doesn't exist, logout
        logout();
      }
      return null;
    }
  }, [token, user?._id, logout]);

  const value = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    isSuperAdmin: user?.supperAdmin || false,
    isVerified: user?.isVerified || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
